import React, { useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, AlertTriangle } from 'lucide-react';
import { useDemoStatus } from '@/hooks/useDemoStatus';
import { supabase } from '@/lib/supabaseClient';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SettingsCard, PrimaryButton } from './primitives';
import { PriyaIntroModal } from '@/components/onboarding/PriyaIntroModal';

/**
 * Phase 84.22 → 84.23.11 · Settings > Help entry for the Sample Data System.
 *
 * Three flows based on current state:
 *   • has_demo=false, has_real=false → single click seeds Priya.
 *   • has_demo=true                  → shows "Clear demo" affordance.
 *   • has_real=true (no demo)        → SIMPLE 2-layer confirm:
 *       L1 (warn): "Demo data replaces existing data"
 *       L2 (impact + checkbox): explicit list of what's removed + a
 *           checkbox reading "I understand that my current data will be
 *           permanently removed". Button enabled only when ticked.
 *       No reason picker — beta feedback (2026-07-28): capturing
 *       *why* someone loads demo is not the churn signal we care
 *       about; capture it in Danger Zone only.
 *       No type-'delete' gate — the explicit checkbox + button label
 *       ("Switch to Demo Account") is enough friction.
 */
export function DemoDataSettingsCard() {
  const { status, loading, seedDemo, clearDemo, refresh } = useDemoStatus();
  const [busy, setBusy] = useState(false);
  const [introOpen, setIntroOpen] = useState(false);
  // Two-layer confirm state for the "has real data" path.
  const [confirmLayer, setConfirmLayer] = useState(0);   // 0 closed, 1 warn, 2 impact+check
  const [acknowledged, setAcknowledged] = useState(false);

  const backend = process.env.REACT_APP_BACKEND_URL;

  if (!status) {
    return (
      <SettingsCard className="p-5">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          Loading demo status…
        </div>
      </SettingsCard>
    );
  }

  const hasDemo = !!status.has_demo;
  const hasReal = !!status.has_real;

  const doSeed = async () => {
    setBusy(true);
    try {
      const res = await seedDemo();
      if (!res?.ok) {
        toast.error(res?.detail?.message || 'Could not create demo data. Please try again.');
        return;
      }
      toast.success('Demo data ready. Meet Priya from Dubai.');
      setIntroOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const doClear = async () => {
    setBusy(true);
    try {
      const res = await clearDemo();
      if (res?.ok) {
        toast.success('Demo data cleared. Reloading your clean overview…');
        // v3.7.3 Phase 84.28.11 — hard reload after clear so every cached
        // slice of dashboard state (briefing hero tabs, accounts, goals,
        // insights) refetches from scratch. Users reported the four demo
        // briefing tabs lingering on the Overview even after a successful
        // clear because the frontend query cache never invalidated.
        setTimeout(() => { window.location.assign('/dashboard'); }, 500);
      } else {
        toast.error('Could not clear demo data. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  const doDestructiveReset = async () => {
    setBusy(true);
    try {
      const { data } = await supabase.auth.getSession();
      const token = data?.session?.access_token;
      if (!token) {
        toast.error('Your session expired. Please sign in again.');
        return;
      }
      // Send an empty body — the backend accepts missing reason and
      // buckets to 'other' in the audit table.
      const r = await fetch(`${backend}/api/onboarding/demo/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok || !json?.ok) {
        toast.error('Could not switch to demo. Please contact support@2120.money if the problem persists.');
        return;
      }
      toast.success('Your data was cleared and Priya\u2019s demo profile was loaded.');
      setConfirmLayer(0);
      setAcknowledged(false);
      await refresh();
      setIntroOpen(true);
    } catch (_e) {
      toast.error('Network error while switching to demo. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const startSeedFlow = () => {
    if (hasReal) {
      setAcknowledged(false);
      setConfirmLayer(1);
    } else {
      doSeed();
    }
  };

  return (
    <>
      <SettingsCard className="p-5" testId="settings-demo-data-card">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4 text-primary">
          <Sparkles className="h-5 w-5" aria-hidden="true" />
        </div>
        <p className="text-sm font-semibold text-charcoal mb-1.5">Explore with demo data</p>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
          Curious what 2120 looks like with real financial data? Load a complete sample account and explore every feature
          before adding your own information.
          <span className="block mt-2">
            <span className="font-medium text-foreground">Please note:</span> While using demo data, you won&rsquo;t be able
            to upload your own bank statements. When you&rsquo;re ready to start with your own finances, simply remove the
            demo data and upload your statements.
          </span>
          {hasReal && !hasDemo && (
            <span className="block mt-2 text-xs">
              <AlertTriangle className="inline h-3.5 w-3.5 mr-1 text-primary" />
              <span className="font-semibold text-foreground">Warning:</span> You already have your own data. Loading the
              demo requires clearing your data first — you&rsquo;ll see a confirmation before anything is removed.
            </span>
          )}
        </p>
        <div className="flex flex-wrap gap-2">
          {hasDemo ? (
            <PrimaryButton
              variant="ghost"
              size="sm"
              onClick={doClear}
              disabled={busy || loading}
              testId="settings-demo-clear"
            >
              {busy ? 'Clearing…' : 'Clear demo data'}
            </PrimaryButton>
          ) : (
            <PrimaryButton
              variant="ghost"
              size="sm"
              onClick={startSeedFlow}
              disabled={busy || loading}
              testId="settings-demo-load"
            >
              {busy ? 'Loading…' : 'Explore Demo Account'}
            </PrimaryButton>
          )}
          {hasDemo && (
            <PrimaryButton
              variant="ghost"
              size="sm"
              onClick={() => setIntroOpen(true)}
              testId="settings-demo-view-intro"
            >
              About the persona
            </PrimaryButton>
          )}
        </div>
      </SettingsCard>

      {/* Layer 1 — Demo data replaces existing data */}
      <AlertDialog
        open={confirmLayer === 1}
        onOpenChange={(o) => { if (!o && confirmLayer === 1 && !busy) setConfirmLayer(0); }}
      >
        <AlertDialogContent data-testid="demo-warn-layer1">
          <AlertDialogHeader>
            <div className="flex items-center gap-2 text-primary">
              <AlertTriangle className="h-5 w-5" />
              <AlertDialogTitle>Demo data replaces existing data</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              You currently have your own data in 2120. Loading the demo will need to remove it first — demo and real data can&rsquo;t coexist. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button
              onClick={() => setConfirmLayer(2)}
              disabled={busy}
              data-testid="demo-warn-layer1-continue"
            >
              Continue
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Layer 2 — Explicit impact + checkbox acknowledgement */}
      <AlertDialog
        open={confirmLayer === 2}
        onOpenChange={(o) => { if (!o && confirmLayer === 2 && !busy) { setConfirmLayer(0); setAcknowledged(false); } }}
      >
        <AlertDialogContent data-testid="demo-warn-layer2">
          <AlertDialogHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              <AlertDialogTitle>Switch to the Demo Account?</AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              To load the demo account, we&rsquo;ll first remove all of your current data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="text-sm text-charcoal space-y-2 mt-1" data-testid="demo-warn-layer2-impact">
            <ul className="list-disc pl-5 space-y-1">
              <li>Accounts</li>
              <li>Transactions</li>
              <li>Goals</li>
              <li>Recurring commitments</li>
              <li>Insights and reflections</li>
            </ul>
            <p className="font-semibold text-destructive">This action cannot be undone.</p>
            <p className="text-muted-foreground">If you want to keep your data, select Cancel.</p>
          </div>
          <label
            htmlFor="demo-ack"
            className="flex items-start gap-2.5 mt-4 cursor-pointer select-none"
            data-testid="demo-warn-layer2-ack-label"
          >
            <Checkbox
              id="demo-ack"
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(v === true)}
              disabled={busy}
              data-testid="demo-warn-layer2-ack"
              className="mt-0.5"
            />
            <span className="text-sm text-charcoal leading-snug">
              I understand that my current data will be permanently removed.
            </span>
          </label>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button
              onClick={doDestructiveReset}
              disabled={busy || !acknowledged}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-40"
              data-testid="demo-warn-layer2-confirm"
            >
              {busy ? 'Switching…' : 'Switch to Demo Account'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PriyaIntroModal open={introOpen} onOpenChange={setIntroOpen} onExplore={() => { setIntroOpen(false); refresh(); }} />
    </>
  );
}
